import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Stats() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats/me");
        setStats(data);
      } catch (_) {}
    })();
  }, []);

  const progressionData = (stats?.progression || []).map((p, i) => ({
    name: `#${i + 1}`,
    score: p.score,
    won: p.won ? 1 : 0,
  }));

  return (
    <AppLayout>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mb-6">{t("stats.title")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label={t("dashboard.stats.games")} value={stats?.games_played ?? 0} testId="stats-games" />
        <MetricCard label={t("dashboard.stats.wins")} value={stats?.wins ?? 0} testId="stats-wins" />
        <MetricCard label={t("stats.winRate")} value={`${stats?.win_rate ?? 0}%`} testId="stats-winrate" />
        <MetricCard label={t("dashboard.stats.best")} value={stats?.best_score ?? 0} testId="stats-best" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">{t("stats.progression")}</CardTitle></CardHeader>
          <CardContent className="h-72">
            {progressionData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">—</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progressionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">Répartition</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: "Total", value: stats?.total_score ?? 0 },
                { name: "Best", value: stats?.best_score ?? 0 },
                { name: "Avg", value: Math.round(stats?.avg_score ?? 0) },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function MetricCard({ label, value, testId }) {
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-6">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</div>
        <div className="mt-2 text-3xl font-display font-extrabold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}
